import React from 'react';
import { 
  Thermometer, 
  Droplets, 
  Battery, 
  MapPin, 
  Wifi,
  WifiOff,
  AlertTriangle,
  Clock
} from 'lucide-react';

const SensorCard = ({ sensor, onClick }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'active':
        return {
          color: 'success',
          icon: Wifi,
          label: 'Actif',
          bgColor: 'from-success-50 to-success-100',
          borderColor: 'border-success-200'
        };
      case 'warning':
        return {
          color: 'warning',
          icon: AlertTriangle,
          label: 'Alerte',
          bgColor: 'from-warning-50 to-warning-100',
          borderColor: 'border-warning-200'
        };
      case 'offline':
        return {
          color: 'danger',
          icon: WifiOff,
          label: 'Hors ligne',
          bgColor: 'from-danger-50 to-danger-100',
          borderColor: 'border-danger-200'
        };
      default:
        return {
          color: 'slate',
          icon: WifiOff,
          label: 'Inconnu',
          bgColor: 'from-slate-50 to-slate-100',
          borderColor: 'border-slate-200'
        };
    }
  };

  const getBatteryLevel = (battery) => {
    if (battery > 50) return { color: 'success', width: '100%' };
    if (battery > 20) return { color: 'warning', width: '60%' };
    return { color: 'danger', width: '30%' };
  };

  const getHumidityStatus = (humidity, threshold) => {
    if (humidity < threshold.humidity_min || humidity > threshold.humidity_max) {
      return { status: 'alert', color: 'danger' };
    }
    return { status: 'normal', color: 'success' };
  };

  const getTemperatureStatus = (temperature, threshold) => {
    if (temperature < threshold.temperature_min || temperature > threshold.temperature_max) {
      return { status: 'alert', color: 'danger' };
    }
    return { status: 'normal', color: 'success' };
  };

  const formatLastUpdate = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} h`;
    return `${Math.floor(diffInMinutes / 1440)} j`;
  };

  const statusConfig = getStatusConfig(sensor.status);
  const batteryLevel = getBatteryLevel(sensor.battery);
  const humidityStatus = getHumidityStatus(sensor.humidity, sensor.alert_threshold);
  const temperatureStatus = getTemperatureStatus(sensor.temperature, sensor.alert_threshold);

  const StatusIcon = statusConfig.icon;

  return (
    <div
      className={`sensor-card cursor-pointer animate-fade-in ${statusConfig.borderColor}`}
      onClick={() => onClick(sensor)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            {sensor.name}
          </h3>
          <div className="flex items-center text-slate-500 text-sm">
            <MapPin className="h-4 w-4 mr-1" />
            {sensor.location}
          </div>
        </div>
        
        <div className="flex flex-col items-end space-y-2">
          <div className={`status-indicator status-${statusConfig.color}`}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {statusConfig.label}
          </div>
          
          {/* Battery indicator */}
          <div className="flex items-center space-x-2">
            <Battery className={`h-4 w-4 text-${batteryLevel.color}-600`} />
            <div className="w-8 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full bg-${batteryLevel.color}-500 transition-all duration-300`}
                style={{ width: `${sensor.battery}%` }}
              />
            </div>
            <span className="text-xs font-medium text-slate-600">
              {sensor.battery}%
            </span>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Humidity */}
        <div className={`metric-card border-l-4 border-${humidityStatus.color}-500`}>
          <div className="flex items-center justify-between mb-2">
            <Droplets className={`h-5 w-5 text-${humidityStatus.color}-600`} />
            {humidityStatus.status === 'alert' && (
              <AlertTriangle className="h-4 w-4 text-danger-500" />
            )}
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-slate-800">
              {sensor.humidity.toFixed(1)}%
            </p>
            <p className="text-xs text-slate-500">Humidité</p>
            <p className="text-xs text-slate-400">
              Seuil: {sensor.alert_threshold.humidity_min}-{sensor.alert_threshold.humidity_max}%
            </p>
          </div>
        </div>

        {/* Temperature */}
        <div className={`metric-card border-l-4 border-${temperatureStatus.color}-500`}>
          <div className="flex items-center justify-between mb-2">
            <Thermometer className={`h-5 w-5 text-${temperatureStatus.color}-600`} />
            {temperatureStatus.status === 'alert' && (
              <AlertTriangle className="h-4 w-4 text-danger-500" />
            )}
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-slate-800">
              {sensor.temperature.toFixed(1)}°C
            </p>
            <p className="text-xs text-slate-500">Température</p>
            <p className="text-xs text-slate-400">
              Seuil: {sensor.alert_threshold.temperature_min}-{sensor.alert_threshold.temperature_max}°C
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <div className="flex items-center text-slate-500 text-sm">
          <Clock className="h-4 w-4 mr-1" />
          Mis à jour il y a {formatLastUpdate(sensor.last_update)}
        </div>
        
        <div className={`px-3 py-1 bg-gradient-to-r ${statusConfig.bgColor} rounded-full border ${statusConfig.borderColor}`}>
          <span className={`text-xs font-medium text-${statusConfig.color}-700`}>
            {sensor.type === 'humidity_temperature' ? 'T°/Humidité' : sensor.type}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SensorCard;